import { useState } from 'react'
import { bgrPsdToBlob, bgrDirectRemove } from '../../utils/bgr'

// Grid order: A(top-L), P(top-R), L(mid-L), R(mid-R), S(bot-L), I(bot-R)
// Filenames: {pageNum}{letter} e.g. 3A.PSD, 10L.PSD, 4R.PSD
const CASE_REPORT_SLOTS = ['A', 'P', 'L', 'R', 'S', 'I']
const CR_SLOT_LABELS = { A: 'Anterior', P: 'Posterior', L: 'Left Lateral', R: 'Right Lateral', S: 'Superior', I: 'Inferior' }

// Pixel geometry at 300 DPI on 2480×3508px A4 (1mm = 11.811px)
const SLOT_RECTS = [
  { x: 95,   y: 661,  w: 1151, h: 819 }, // A  top-left
  { x: 1246, y: 661,  w: 1151, h: 819 }, // P  top-right
  { x: 95,   y: 1480, w: 1151, h: 819 }, // L  mid-left
  { x: 1246, y: 1480, w: 1151, h: 819 }, // R  mid-right
  { x: 95,   y: 2299, w: 1151, h: 819 }, // S  bot-left
  { x: 1246, y: 2299, w: 1151, h: 819 }, // I  bot-right
]

// Parse "{digits}{letter}" → { page: '3', key: 'A' } for '3A.PSD'
function crParseFilename(filename) {
  const base = filename.replace(/\.[^.]+$/, '').trim().toUpperCase()
  const m = base.match(/^(\d*)([A-Z]+)$/)
  if (!m) return null
  const key = m[2]
  if (!CASE_REPORT_SLOTS.includes(key)) return null
  return { page: m[1] || '1', key }
}

function loadImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => { URL.revokeObjectURL(url); resolve(img) }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image decode failed')) }
    img.src = url
  })
}

function drawCentered(ctx, img, rect) {
  // 1:1 pixels, centered — sides clip naturally since bg is removed
  ctx.save()
  ctx.beginPath()
  ctx.rect(rect.x, rect.y, rect.w, rect.h)
  ctx.clip()
  const dx = rect.x + (rect.w - img.naturalWidth) / 2
  const dy = rect.y + (rect.h - img.naturalHeight) / 2
  ctx.drawImage(img, dx, dy)
  ctx.restore()
}

async function crLoadTemplate() {
  return new Promise((res, rej) => {
    const img = new Image()
    img.onload = () => res(img)
    img.onerror = () => rej(new Error('Template failed to load'))
    img.src = '/case-report-template.png'
  })
}

function CaseReportTab() {
  // fileGroups: { pageNum: { A: File, P: File, L: File, R: File, S: File, I: File } }
  const [fileGroups, setFileGroups] = useState({})
  const [unmatched, setUnmatched] = useState([])
  const [removeBg, setRemoveBg] = useState(true)
  const [tolerance, setTolerance] = useState(35)
  const [outputDirHandle, setOutputDirHandle] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [progress, setProgress] = useState(null) // { current, total, name }
  const [done, setDone] = useState(null)          // { count, errors }
  const hasFSA = 'showDirectoryPicker' in window

  function onFilesChange(e) {
    const files = Array.from(e.target.files)
    e.target.value = ''
    const newGroups = { ...fileGroups }
    const missed = []

    for (const file of files) {
      const parsed = crParseFilename(file.name)
      if (!parsed) { missed.push(file.name); continue }
      const { page, key } = parsed
      if (!newGroups[page]) newGroups[page] = {}
      newGroups[page][key] = file
    }

    setFileGroups(newGroups)
    setUnmatched(missed)
    setDone(null)
  }

  async function pickOutputFolder() {
    try {
      const h = await window.showDirectoryPicker({ mode: 'readwrite' })
      setOutputDirHandle(h)
    } catch (err) {
      if (err.name !== 'AbortError') alert(`Failed: ${err.message}`)
    }
  }

  const pageNums = Object.keys(fileGroups).sort((a, b) => Number(a) - Number(b))
  const validPages = pageNums.filter(p => CASE_REPORT_SLOTS.every(s => fileGroups[p]?.[s]))
  const partialPages = pageNums.filter(p => !validPages.includes(p))

  async function generate() {
    setGenerating(true)
    setDone(null)
    const { readPsd } = await import('ag-psd')
    let outputDir = null
    if (outputDirHandle) {
      outputDir = await outputDirHandle.getDirectoryHandle('output', { create: true })
    }
    const tpl = await crLoadTemplate()
    const errors = []

    for (let pi = 0; pi < validPages.length; pi++) {
      const pageNum = validPages[pi]
      setProgress({ current: pi + 1, total: validPages.length, name: `Page ${pageNum}` })
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 2480; canvas.height = 3508
        const ctx = canvas.getContext('2d')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, 2480, 3508)
        ctx.drawImage(tpl, 0, 0, 2480, 3508)

        for (let si = 0; si < CASE_REPORT_SLOTS.length; si++) {
          const key = CASE_REPORT_SLOTS[si]
          const file = fileGroups[pageNum][key]
          let blob = file.name.toLowerCase().endsWith('.psd')
            ? await bgrPsdToBlob(file, readPsd)
            : file
          if (removeBg) blob = await bgrDirectRemove(blob, tolerance)
          const img = await loadImage(blob)
          drawCentered(ctx, img, SLOT_RECTS[si])
        }

        const finalBlob = await new Promise(res => canvas.toBlob(res, 'image/png'))
        const outName = `CaseReport_${pageNum}.png`

        if (outputDir) {
          const fh = await outputDir.getFileHandle(outName, { create: true })
          const w = await fh.createWritable()
          await w.write(finalBlob); await w.close()
        } else {
          const url = URL.createObjectURL(finalBlob)
          const a = document.createElement('a'); a.href = url; a.download = outName; a.click()
          setTimeout(() => URL.revokeObjectURL(url), 5000)
          if (pi < validPages.length - 1) await new Promise(r => setTimeout(r, 600))
        }
      } catch (err) {
        errors.push(`Page ${pageNum}: ${err.message}`)
      }
    }

    setProgress(null)
    setDone({ count: validPages.length - errors.length, errors })
    setGenerating(false)
  }

  return (
    <div className="space-y-4">
      {/* Controls bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <label className={['btn-primary cursor-pointer text-sm px-4 py-2', generating ? 'opacity-50 pointer-events-none' : ''].join(' ')}>
          Upload Images…
          <input type="file" className="hidden" accept="image/*,.psd" multiple onChange={onFilesChange} disabled={generating} />
        </label>

        <span className="text-xs text-gray-400">
          Files named <span className="font-mono">3A.PSD</span>, <span className="font-mono">3P.PSD</span>, … — PNG, JPG or PSD
        </span>

        <div className="w-px h-5 bg-gray-200" />

        <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={removeBg} onChange={e => setRemoveBg(e.target.checked)} disabled={generating} />
          Remove BG
        </label>

        {removeBg && (
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            Tolerance
            <input type="number" min={10} max={80} step={5} value={tolerance}
              onChange={e => setTolerance(Math.min(80, Math.max(10, Number(e.target.value))))}
              disabled={generating}
              className="w-14 border border-gray-300 rounded px-1 py-0.5 text-sm text-center" />
          </label>
        )}

        <div className="w-px h-5 bg-gray-200" />

        {hasFSA ? (
          <button onClick={pickOutputFolder} disabled={generating} className="btn-secondary text-sm px-3 py-2">
            {outputDirHandle ? `Output: ${outputDirHandle.name}` : 'Output folder…'}
          </button>
        ) : (
          <span className="text-xs text-gray-400">Output: download links</span>
        )}

        <div className="flex-1" />

        <button
          onClick={generate}
          disabled={validPages.length === 0 || generating}
          className="btn-primary text-sm px-5 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {generating
            ? progress ? `${progress.name} (${progress.current}/${progress.total})…` : 'Starting…'
            : `Generate ${validPages.length > 1 ? `${validPages.length} Reports` : 'Report'}`}
        </button>
      </div>

      {/* Page summary table */}
      {pageNums.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Detected pages — {validPages.length} ready, {partialPages.length} incomplete
          </h3>
          <div className="overflow-x-auto">
            <table className="text-xs w-full">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100">
                  <th className="text-left py-1.5 pr-4 font-medium">Page</th>
                  {CASE_REPORT_SLOTS.map(s => (
                    <th key={s} className="text-center px-2 font-medium">{s}</th>
                  ))}
                  <th className="text-left pl-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {pageNums.map(p => {
                  const g = fileGroups[p]
                  const complete = CASE_REPORT_SLOTS.every(s => g?.[s])
                  return (
                    <tr key={p} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-1.5 pr-4 font-mono font-semibold text-gray-700">{p}</td>
                      {CASE_REPORT_SLOTS.map(s => (
                        <td key={s} className="text-center px-2">
                          {g?.[s]
                            ? <span className="text-green-500">✓</span>
                            : <span className="text-red-400">✗</span>}
                        </td>
                      ))}
                      <td className="pl-4">
                        {complete
                          ? <span className="text-green-600 font-medium">Ready</span>
                          : <span className="text-amber-500">Missing: {CASE_REPORT_SLOTS.filter(s => !g?.[s]).join(', ')}</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unmatched warning */}
      {unmatched.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 text-sm text-yellow-800">
          Unrecognised filenames: {unmatched.join(', ')}
        </div>
      )}

      {/* Done summary */}
      {done && (
        <div className={['rounded-lg px-4 py-3 text-sm', done.errors.length ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' : 'bg-green-50 border border-green-200 text-green-800'].join(' ')}>
          {done.count} report{done.count !== 1 ? 's' : ''} generated.
          {done.errors.length > 0 && <> Errors: {done.errors.join('; ')}</>}
        </div>
      )}
    </div>
  )
}

export default CaseReportTab
